/**
 * Admin Super-Powers — "Create user" modal (super_admin, `users:create`).
 *
 * Composes Modal + FormField + TextInput + Select. Client-side validation via
 * the shared `AdminCreateUserInputSchema` from @bravemove/types so the dialog
 * cannot submit obviously-invalid data; the server is still the source of truth
 * (rate limits, 409 EMAIL_TAKEN).
 *
 * Two phases:
 *   1. The create form. On success it flips to →
 *   2. The success state: shows the one-time activation URL (copy button) +
 *      "Activation email sent to <email>" + a link to open the new user's detail
 *      page. 409 EMAIL_TAKEN surfaces inline; everything else is a generic error.
 *
 * Business roles are NOT settable here — the consumer user is created in
 * `pending_verification`; a B2B actor is provisioned separately on the detail
 * page (Create business).
 *
 * Gated by `users:create` — the parent list page hides the trigger button for
 * any actor lacking the permission.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, Select, TextInput } from '@bravemove/ui-web';
import { AdminCreateUserInputSchema, type AdminCreateUserInput } from '@bravemove/types';
import { ApiError } from '../../auth/types';
import { translateApiError } from '../../auth/errors.js';
import type { AdminCreateUserResult } from '../../api/adminUsers';

export interface CreateUserDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Wired to `adminUsersApi.createUser` by the parent (real or fixture). */
  readonly onCreate: (input: AdminCreateUserInput) => Promise<AdminCreateUserResult>;
}

const LOCALES: readonly { value: 'en' | 'ar' | 'fr'; labelKey: string; fallback: string }[] = [
  { value: 'en', labelKey: 'admin:f43fc.createUser.locale_en', fallback: 'English' },
  { value: 'ar', labelKey: 'admin:f43fc.createUser.locale_ar', fallback: 'Arabic' },
  { value: 'fr', labelKey: 'admin:f43fc.createUser.locale_fr', fallback: 'French' },
];

export function CreateUserDialog({ open, onClose, onCreate }: CreateUserDialogProps) {
  const { t } = useI18n();

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');
  const [locale, setLocale] = useState<'en' | 'ar' | 'fr'>('en');
  const [countryCode, setCountryCode] = useState('LB');

  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  /** Set on success — flips the modal from the form to the activation-link view. */
  const [created, setCreated] = useState<AdminCreateUserResult | null>(null);
  const [copied, setCopied] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setEmail('');
    setDisplayName('');
    setPhone('');
    setLocale('en');
    setCountryCode('LB');
    setSubmitting(false);
    setServerError(null);
    setCreated(null);
    setCopied(false);
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const validation = useMemo(() => {
    const candidate = {
      email: email.trim(),
      displayName: displayName.trim(),
      phone: phone.trim() === '' ? undefined : phone.trim(),
      locale,
      countryCode: countryCode.trim() === '' ? undefined : countryCode.trim().toUpperCase(),
    };
    return AdminCreateUserInputSchema.safeParse(candidate);
  }, [email, displayName, phone, locale, countryCode]);

  const disableSubmit = submitting || !validation.success;

  const submit = async () => {
    if (!validation.success) return;
    setServerError(null);
    setSubmitting(true);
    try {
      const result = await onCreate(validation.data);
      setCreated(result);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'EMAIL_TAKEN') {
        setServerError(
          t('admin:f43fc.createUser.error_email_taken', {
            defaultValue: 'A user with this email already exists.',
          }),
        );
      } else {
        setServerError(translateApiError(err, t));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const copyActivationUrl = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.activationUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // No clipboard permission — silently ignore (the URL is shown read-only).
    }
  };

  // ── Phase 2 — success / activation-link view ──────────────────────────────
  if (created) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title={t('admin:f43fc.createUser.success_title', { defaultValue: 'User created' })}
        size="md"
        footer={
          <Button variant="primary" onClick={onClose} data-testid="create-user-done">
            {t('admin:f43fc.createUser.done', { defaultValue: 'Done' })}
          </Button>
        }
      >
        <div className="flex flex-col gap-4" data-testid="create-user-success">
          <div data-testid="create-user-email-sent">
            <Banner tone="success">
              {t('admin:f43fc.createUser.email_sent', {
                defaultValue: 'Activation email sent to {email}.',
                email: created.user.email,
              })}
            </Banner>
          </div>

          <FormField
            label={t('admin:f43fc.createUser.activation_url_label', {
              defaultValue: 'Activation link',
            })}
            helper={t('admin:f43fc.createUser.activation_url_helper', {
              defaultValue:
                'One-time link the user redeems to set up their account. Share it directly if email is delayed.',
            })}
          >
            <div className="flex items-center gap-2">
              <TextInput
                value={created.activationUrl}
                readOnly
                data-testid="create-user-activation-url"
                aria-label={t('admin:f43fc.createUser.activation_url_label', {
                  defaultValue: 'Activation link',
                })}
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void copyActivationUrl()}
                data-testid="create-user-copy"
              >
                {copied
                  ? t('admin:common.copied', { defaultValue: 'Copied' })
                  : t('admin:common.copy', { defaultValue: 'Copy' })}
              </Button>
            </div>
          </FormField>

          <Link
            to={`/users/${created.user.id}`}
            className="text-[13px] font-medium text-accent underline"
            data-testid="create-user-open-detail"
            onClick={onClose}
          >
            {t('admin:f43fc.createUser.open_detail', {
              defaultValue: 'Open the new user',
            })}
          </Link>
        </div>
      </Modal>
    );
  }

  // ── Phase 1 — the create form ─────────────────────────────────────────────
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:f43fc.createUser.title', { defaultValue: 'Create user' })}
      description={t('admin:f43fc.createUser.description', {
        defaultValue:
          'Creates a consumer account in pending-verification and emails an activation link. Business roles are added separately by creating a business.',
      })}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="create-user-cancel"
          >
            {t('admin:common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            disabled={disableSubmit}
            data-testid="create-user-submit"
          >
            {t('admin:f43fc.createUser.submit', { defaultValue: 'Create user' })}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        data-testid="create-user-dialog"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <FormField
          label={t('admin:f43fc.createUser.email_label', { defaultValue: 'Email' })}
          required
        >
          <TextInput
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('admin:f43fc.createUser.email_placeholder', {
              defaultValue: 'user@example.com',
            })}
            data-testid="create-user-email"
            aria-label={t('admin:f43fc.createUser.email_label', { defaultValue: 'Email' })}
          />
        </FormField>

        <FormField
          label={t('admin:f43fc.createUser.displayName_label', { defaultValue: 'Display name' })}
          required
        >
          <TextInput
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={120}
            data-testid="create-user-displayName"
            aria-label={t('admin:f43fc.createUser.displayName_label', {
              defaultValue: 'Display name',
            })}
          />
        </FormField>

        <FormField
          label={t('admin:f43fc.createUser.phone_label', { defaultValue: 'Phone (optional)' })}
          helper={t('admin:f43fc.createUser.phone_helper', {
            defaultValue: 'E.164 format, e.g. +9613000000.',
          })}
        >
          <TextInput
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+9613000000"
            data-testid="create-user-phone"
            aria-label={t('admin:f43fc.createUser.phone_label', {
              defaultValue: 'Phone (optional)',
            })}
          />
        </FormField>

        <div className="grid grid-cols-2 gap-4">
          <FormField label={t('admin:f43fc.createUser.locale_label', { defaultValue: 'Locale' })}>
            <Select
              value={locale}
              onChange={(e) => setLocale(e.target.value as 'en' | 'ar' | 'fr')}
              options={LOCALES.map((l) => ({
                value: l.value,
                label: t(l.labelKey, { defaultValue: l.fallback }),
              }))}
              data-testid="create-user-locale"
              aria-label={t('admin:f43fc.createUser.locale_label', { defaultValue: 'Locale' })}
            />
          </FormField>

          <FormField
            label={t('admin:f43fc.createUser.country_label', {
              defaultValue: 'Country (optional)',
            })}
            helper={t('admin:f43fc.createUser.country_helper', {
              defaultValue: 'ISO code, e.g. LB.',
            })}
          >
            <TextInput
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              maxLength={2}
              data-testid="create-user-country"
              aria-label={t('admin:f43fc.createUser.country_label', {
                defaultValue: 'Country (optional)',
              })}
            />
          </FormField>
        </div>

        {serverError ? (
          <div data-testid="create-user-server-error">
            <Banner tone="danger">{serverError}</Banner>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}

export default CreateUserDialog;
